import React, { useEffect, useState } from 'react'
import { getCodes } from 'country-list'
import Select from '@8x8/oxygen-select'

import { updatePaymentInfo, fetchDefaultPaymentMethod } from '../../services/api'
import { isValidEmail } from '~/core/utils'
import Modal from '~/components/common/modal'
import StripeForm from './stripe-form'
import { translate } from '~/core/translate'
import { COUNTRIES_WITH_ZIP_TAX } from '~/core/constants'

import {
  Container,
  Error,
  Label,
  StripeInput,
  StyledCloseLargeIcon,
  TitleContainer,
  Title,
  ZipCountryPane,
  Subpane
} from './styled'

const countriesArray = getCodes().map(code => ({ value: code, label: code }))

const defaultPaymentError = {
  cardNumber: {
    complete: false,
    error: ''
  },
  cardCvc: {
    complete: false,
    error: ''
  },
  cardExpiry: {
    complete: false,
    error: ''
  },
  email: {
    complete: false,
    error: ''
  },
  zip: {
    complete: false,
    error: ''
  },
  submit: {
    complete: true,
    error: ''
  }
}

const getErrorMessage = info =>
  info.email.error ||
  info.cardNumber.error ||
  info.cardExpiry.error ||
  info.cardCvc.error ||
  info.zip.error ||
  info.submit.error

const getIsEmailError = info => info.email.error

const getIsValid = info => Object.values(info)
  .every(value => value.complete)

const EditCardInfoDialog = ({ userInfo, onClose, onSuccess }) => {
  const [paymentError, setPaymentError] = useState(defaultPaymentError)
  const errorMessage = getErrorMessage(paymentError)
  const isEmailError = getIsEmailError(paymentError)
  const isValid = getIsValid(paymentError)
  const { email, isEmailVerified } = userInfo
  const [mail, setMail] = useState(email)
  const [country, setCountry] = useState({ value: 'US', label: 'US' })
  const [zip, setZip] = useState('')
  const [shouldShowZip, setShouldShowZip] = useState(true)

  useEffect(() => {
    fetchDefaultPaymentMethod()
      .then(data => {
        setCountry(data.country)
        setZip(data.zip)
        const isCountryWithZip = COUNTRIES_WITH_ZIP_TAX.includes(data.country.value)
        const isZipError = Boolean(isCountryWithZip && !data.zip)

        setShouldShowZip(isCountryWithZip)
        setPaymentError({
          ...paymentError,
          zip: {
            complete: !isZipError,
            error: isZipError ? 'Invalid zip' : ''
          },
          email: {
            complete: Boolean(email),
            error: email ? '' : 'Invalid email'
          }
        })
      })
  }, [])

  const onCountryChange = data => {
    setCountry(data)
    const isCountryWithZip = COUNTRIES_WITH_ZIP_TAX.includes(data.value)
    const isZipError = Boolean(isCountryWithZip && !zip)

    if (!isCountryWithZip) {
      setZip('')
    }

    setShouldShowZip(isCountryWithZip)
    setPaymentError({
      ...paymentError,
      zip: {
        complete: !isZipError,
        error: isZipError ? 'Invalid zip' : ''
      }
    })
  }

  const onZipChange = event => {
    const zipCode = event.target.value.trim()
    setZip(zipCode)
    setPaymentError({
      ...paymentError,
      zip: {
        complete: Boolean(zipCode),
        error: zipCode ? '' : 'Invalid zip'
      }
    })
  }

  const close = () => {
    setPaymentError(defaultPaymentError)
    onClose()
  }

  const onChange = event => {
    setPaymentError({
      ...paymentError,
      [event.elementType]: {
        complete: event.complete,
        error: event.error ? (event.error.message || translate('generic_error')) : ''
      }
    })
  }

  const onEmailBlur = () => {
    if (isValidEmail(mail)) {
      setPaymentError({
        ...paymentError,
        email: {
          complete: true,
          error: ''
        }
      })
      return
    }

    setPaymentError({
      ...paymentError,
      email: {
        complete: false,
        error: 'Invalid email'
      }
    })
  }

  const onEmailChange = event => {
    const email = event.target.value
    setMail(email.trim().toLowerCase())
  }

  const onSubmit = async cardElement => {
    try {
      await updatePaymentInfo({
        cardElement,
        zip,
        country: country.value
      })
      onSuccess()
    } catch (e) {
      setPaymentError({
        ...paymentError,
        submit: {
          complete: true,
          error: e?.message || translate('generic_error')
        }
      })
    }
  }

  return (
    <Modal size='small' visible width='375px' height='auto' onClose={onClose}>
      <Container>
        <TitleContainer>
          <Title>{translate('change_payment_details')}</Title>
          <StyledCloseLargeIcon onClick={close} />
        </TitleContainer>
        <Label>
          {`${translate('email')}:`}
        </Label>
        <StripeInput
          invalid={isEmailError}
          placeholder='email@example.com'
          onChange={onEmailChange}
          onBlur={onEmailBlur}
          value={mail}
          disabled={isEmailVerified}
        />
        <ZipCountryPane>
          <Subpane>
            <Label>
              {`${translate('country')}:`}
            </Label>
            <Select
              options={countriesArray}
              onChange={onCountryChange}
              value={country}
              isClearable={false}
              theme={{
                fontSize: '16px',
                optionTextFontSize: '16px'
              }}
            />
          </Subpane>
          {shouldShowZip && (
            <Subpane>
              <Label>
                {`${translate('zip')}:`}
              </Label>
              <StripeInput
                placeholder='zip'
                onChange={onZipChange}
                value={zip}
              />
            </Subpane>
          )}
        </ZipCountryPane>
        <StripeForm
          onChange={onChange}
          onSubmit={onSubmit}
          submitDisabled={!isValid}
        />
        <Error>{errorMessage}</Error>
      </Container>
    </Modal>
  )
}

export default EditCardInfoDialog
